import { io, Socket } from "socket.io-client";
import { store } from "src/redux/store";

type SocketAckSuccess<T = any> = {
  ok: true;
  data: T;
};

type SocketAckError = {
  ok: false;
  error?: string;
};

type SocketAckResponse<T = any> = SocketAckSuccess<T> | SocketAckError;

const DEFAULT_ACK_TIMEOUT_MS = 10000;

let socket: Socket | null = null;
let lastToken: string | null = null;

const getSocketUrl = () =>
  process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || "";

const getAuthToken = () => store.getState().auth?.accessToken || "";

const ensureSocketAuth = (client: Socket) => {
  const token = getAuthToken();
  if (token === lastToken) return;

  lastToken = token;
  client.auth = { token };

  // Reconnect to refresh handshake auth when token changes.
  if (client.connected) {
    client.disconnect();
  }

  if (!client.connected && !client.active) {
    client.connect();
  }
};

export const getSocketClient = () => {
  const socketUrl = getSocketUrl();

  if (!socketUrl) {
    throw new Error(
      "Socket URL não configurada. Defina REACT_APP_SOCKET_URL ou REACT_APP_API_URL."
    );
  }

  if (!socket) {
    const token = getAuthToken();
    lastToken = token;

    socket = io(socketUrl, {
      autoConnect: false,
      transports: ["websocket", "polling"],
      auth: { token },
    });
  }

  ensureSocketAuth(socket);

  if (!socket.connected && !socket.active) {
    socket.connect();
  }

  return socket;
};

export const emitSocketAck = async <T = any>(
  event: string,
  payload?: any,
  timeoutMs: number = DEFAULT_ACK_TIMEOUT_MS
): Promise<T> => {
  const client = getSocketClient();

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      callback();
    };

    const timer = window.setTimeout(() => {
      finish(() => {
        reject(new Error(`Socket ack timeout (${event})`));
      });
    }, timeoutMs);

    try {
      client.emit(event, payload ?? {}, (ack: SocketAckResponse<T>) => {
        finish(() => {
          if (!ack || typeof ack !== "object") {
            reject(new Error(`Socket ack inválido (${event})`));
            return;
          }

          if ((ack as SocketAckError).ok === false) {
            reject(
              new Error(
                (ack as SocketAckError).error ||
                  `Socket request falhou (${event})`
              )
            );
            return;
          }

          resolve((ack as SocketAckSuccess<T>).data);
        });
      });
    } catch (error) {
      finish(() => {
        reject(
          error instanceof Error
            ? error
            : new Error(`Erro ao emitir socket event (${event})`)
        );
      });
    }
  });
};
